import type { AnyIcon } from '@components/icon/Icon';

export interface TableTagCategory {
  category: string;
  iconName: AnyIcon;
  tags: ReadonlyArray<{ label: string }>;
}

export interface TableTagOption {
  label: string;
  categoryIconName: AnyIcon;
}

/** Preset tags grouped by category; one icon per category (guest + admin UI). */
export const TABLE_TAG_OPTIONS_BY_CATEGORY: ReadonlyArray<TableTagCategory> = [
  {
    category: 'Seating & Location',
    iconName: 'chair',
    tags: [
      { label: 'Corner booth' },
      { label: 'Bar adjacent' },
      { label: 'Garden / patio' },
      { label: 'Private alcove' },
      { label: 'Near entrance' },
      { label: 'Banquette seating' },
      { label: 'Chef counter' },
      { label: 'Near kitchen' },
    ],
  },
  {
    category: 'Sound',
    iconName: 'sound',
    tags: [{ label: 'Lively atmosphere' }, { label: 'Quiet area' }],
  },
  {
    category: 'Lighting',
    iconName: 'sun',
    tags: [{ label: 'Soft lighting' }],
  },
  {
    category: 'Views',
    iconName: 'eye',
    tags: [
      { label: 'People watching' },
      { label: 'Window view' },
      { label: 'Water views' },
      { label: 'Street view' },
    ],
  },
];

export const TABLE_TAG_OPTIONS: ReadonlyArray<TableTagOption> =
  TABLE_TAG_OPTIONS_BY_CATEGORY.flatMap((c) =>
    c.tags.map((t) => ({ label: t.label, categoryIconName: c.iconName })),
  );

const OTHER_CATEGORY = 'Other';

const CATEGORY_BY_LABEL = new Map<string, string>();
const ICON_BY_CATEGORY = new Map<string, AnyIcon>();

for (const group of TABLE_TAG_OPTIONS_BY_CATEGORY) {
  ICON_BY_CATEGORY.set(group.category, group.iconName);
  for (const tag of group.tags) {
    CATEGORY_BY_LABEL.set(tag.label, group.category);
  }
}

const FALLBACK_CATEGORY_ICON: AnyIcon = 'square';

export const getCategoryNameForTableTag = (label: string): string | undefined =>
  CATEGORY_BY_LABEL.get(label);

export const getIconNameForTableTag = (label: string): AnyIcon => {
  const category = getCategoryNameForTableTag(label);
  if (!category) {
    return FALLBACK_CATEGORY_ICON;
  }
  return ICON_BY_CATEGORY.get(category) ?? FALLBACK_CATEGORY_ICON;
};

export const groupSelectedTableTagsByCategory = (
  selectedLabels: string[],
): Array<{ category: string; iconName: AnyIcon; labels: string[] }> => {
  const buckets = new Map<string, string[]>();
  for (const label of selectedLabels) {
    const category = getCategoryNameForTableTag(label) ?? OTHER_CATEGORY;
    const list = buckets.get(category) ?? [];
    list.push(label);
    buckets.set(category, list);
  }

  const orderedCategories = [
    ...TABLE_TAG_OPTIONS_BY_CATEGORY.map((c) => c.category),
    OTHER_CATEGORY,
  ];

  const result: Array<{
    category: string;
    iconName: AnyIcon;
    labels: string[];
  }> = [];

  for (const category of orderedCategories) {
    const labels = buckets.get(category);
    if (!labels?.length) {
      continue;
    }
    const iconName =
      category === OTHER_CATEGORY
        ? FALLBACK_CATEGORY_ICON
        : (ICON_BY_CATEGORY.get(category) ?? FALLBACK_CATEGORY_ICON);
    result.push({ category, iconName, labels });
  }

  return result;
};
